import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Calculator, Euro, Trash2 } from 'lucide-react';
import { getParticipants } from '../lib/services/participantsService';
import { 
  getDepensePartageeById, 
  updateDepensePartagee, 
  deleteDepensePartagee 
} from '../lib/services/depensesPartageesService';
import { 
  Participant, 
  CreateParticipationData, 
  DepensePartagee 
} from '../types/shared-expenses';
import { cleanNumericInput, isValidNumericValue } from '../lib/utils/numberUtils';

const EditSharedExpense: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [description, setDescription] = useState('');
  const [montantTotal, setMontantTotal] = useState('');
  const [date, setDate] = useState<string>('');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantsMontants, setParticipantsMontants] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [depense, setDepense] = useState<DepensePartagee | null>(null);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    setLoadingData(true);
    try {
      // Charger la dépense et les participants en parallèle
      const [depenseResult, participantsResult] = await Promise.all([
        getDepensePartageeById(id!),
        getParticipants()
      ]);

      if (depenseResult.error) throw depenseResult.error;
      if (participantsResult.error) throw participantsResult.error;

      if (depenseResult.data) {
        setDepense(depenseResult.data);
        setDescription(depenseResult.data.description);
        setMontantTotal(depenseResult.data.montant_total.toString());
        setDate(depenseResult.data.date);
      }

      if (participantsResult.data) {
        setParticipants(participantsResult.data);
        
        // Initialiser les montants avec les valeurs existantes ou 0
        const montants: Record<string, string> = {};
        participantsResult.data.forEach(participant => {
          const participation = depenseResult.data?.participations?.find((p: any) => p.participant_id === participant.id);
          montants[participant.id] = participation ? participation.montant_paye.toString() : '0';
        });
        setParticipantsMontants(montants);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      alert('Impossible de charger les données de la dépense partagée.');
      navigate('/shared-expenses');
    } finally {
      setLoadingData(false);
    }
  };

  const handleMontantChange = (montant: string, participantId: string) => {
    const cleanedValue = montant.replace(/[^0-9.]/g, '');
    // Supprimer les zéros non significatifs
    const finalValue = cleanNumericInput(cleanedValue);
    
    setParticipantsMontants(prev => ({
      ...prev,
      [participantId]: finalValue
    }));
  };

  const calculerMontantAutre = () => {
    if (!isValidNumericValue(montantTotal)) {
      alert('Veuillez d\'abord saisir un montant total valide.');
      return;
    }
    
    const montantTotalValue = parseFloat(montantTotal);
    if (montantTotalValue <= 0) {
      alert('Le montant total doit être supérieur à 0.');
      return;
    }

    if (participants.length < 2) {
      alert('Il faut au moins 2 participants pour utiliser cette fonction.');
      return;
    }

    const participantAvecMontant = participants.find(p => {
      const montant = participantsMontants[p.id];
      return montant && parseFloat(montant) > 0;
    });

    if (!participantAvecMontant) {
      alert('Veuillez d\'abord saisir un montant pour un participant.');
      return;
    }

    const total = montantTotalValue;
    const montantPremier = parseFloat(participantsMontants[participantAvecMontant.id] || '0');
    const montantAutre = (total - montantPremier).toFixed(2);
    
    const autreParticipant = participants.find(p => p.id !== participantAvecMontant.id);
    
    if (autreParticipant) {
      setParticipantsMontants(prev => ({
        ...prev,
        [autreParticipant.id]: montantAutre
      }));
    }
  };

  const calculerTotal = () => {
    let total = 0;
    
    Object.values(participantsMontants).forEach(montant => {
      const valeur = parseFloat(montant);
      if (!isNaN(valeur)) {
        total += valeur;
      }
    });
    
    return total.toFixed(2);
  };

  const handleUpdateDepense = () => {
    if (loading) return;
    
    if (!description.trim()) {
      alert('Veuillez saisir une description.');
      return;
    }
    
    if (!isValidNumericValue(montantTotal)) {
      alert('Le montant total doit être un nombre valide.');
      return;
    }
    
    const montantTotalValue = parseFloat(montantTotal);
    if (montantTotalValue <= 0) {
      alert('Le montant total doit être supérieur à 0.');
      return;
    }
    
    const totalPaye = parseFloat(calculerTotal());
    const total = montantTotalValue;
    
    if (Math.abs(totalPaye - total) > 0.01) {
      alert(
        `Totaux incorrects : la somme payée (${totalPaye.toFixed(2)} €) ne correspond pas au montant total (${total.toFixed(2)} €).\nCorrigez les montants avant d'enregistrer.`
      );
      return;
    }
    
    updateDepense();
  };

  const updateDepense = async () => {
    setLoading(true);
    try {
      // S'assurer que le montant total est valide
      if (!isValidNumericValue(montantTotal)) {
        alert('Le montant total doit être un nombre valide.');
        setLoading(false);
        return;
      }
      
      const depenseData = {
        date,
        description,
        montant_total: parseFloat(montantTotal)
      };
      
      const participations: CreateParticipationData[] = [];
      
      participants.forEach(participant => {
        const montant = participantsMontants[participant.id];
        
        if (!montant || isNaN(parseFloat(montant)) || parseFloat(montant) === 0) {
          participations.push({
            participant_id: participant.id,
            montant_paye: 0
          });
        } else {
          participations.push({
            participant_id: participant.id,
            montant_paye: parseFloat(montant)
          });
        }
      });
      
      const { error } = await updateDepensePartagee(id!, depenseData, participations);
      
      if (error) throw error;
      
      alert('La dépense partagée a été mise à jour avec succès.');
      navigate('/shared-expenses');
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la dépense:', error);
      alert('Impossible de mettre à jour la dépense partagée.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDepense = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette dépense partagée ?')) {
      return;
    }

    try {
      const { error } = await deleteDepensePartagee(id!);
      if (error) throw error;
      
      alert('La dépense partagée a été supprimée avec succès.');
      navigate('/shared-expenses');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression de la dépense partagée');
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-dark-300">Chargement des données...</p>
        </div>
      </div>
    );
  }

  if (!depense) {
    return (
      <div className="text-center py-16">
        <p className="text-dark-300">Dépense non trouvée</p>
        <button
          onClick={() => navigate('/shared-expenses')}
          className="mt-4 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-medium transition-colors duration-200"
        >
          Retour aux dépenses partagées
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/shared-expenses')}
            className="p-2 text-dark-300 hover:text-white hover:bg-dark-800 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">Modifier la dépense partagée</h1>
            <p className="text-dark-300 mt-2">Modifiez les informations de votre dépense partagée</p>
          </div>
        </div>
        
        <button
          onClick={handleDeleteDepense}
          className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200"
        >
          <Trash2 className="w-4 h-4" />
          <span>Supprimer</span>
        </button>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-dark-800 rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-white">Mise à jour en cours...</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Formulaire principal */}
        <div className="space-y-6">
          <div className="bg-dark-900 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Informations générales</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Description de la dépense"
                  className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Montant total
                </label>
                <div className="relative">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={montantTotal}
                    onChange={(e) => {
                      const cleaned = cleanNumericInput(e.target.value.replace(/[^0-9.]/g, ''));
                      setMontantTotal(cleaned);
                    }}
                    placeholder="0.00"
                    className="w-full pl-12 pr-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                    <Euro className="w-5 h-5 text-dark-400" />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section répartition */}
        <div className="space-y-6">
          <div className="bg-dark-900 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">Répartition</h2>
              <button
                onClick={calculerMontantAutre}
                className="flex items-center space-x-2 px-4 py-2 bg-dark-800 hover:bg-dark-700 text-primary-500 rounded-lg transition-colors duration-200"
              >
                <Calculator className="w-4 h-4" />
                <span className="text-sm font-medium">Calculer reste</span>
              </button>
            </div>
            
            <div className="space-y-4">
              {participants.map(participant => (
                <div key={participant.id} className="flex items-center justify-between p-4 bg-dark-800 rounded-lg">
                  <span className="text-white font-medium">{participant.nom}</span>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={participantsMontants[participant.id] || ''}
                      onChange={(e) => handleMontantChange(e.target.value, participant.id)}
                      placeholder="0.00"
                      className="w-24 px-3 py-2 bg-dark-700 border border-dark-600 rounded text-white text-right placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <span className="text-white">€</span>
                  </div>
                </div>
              ))}
              
              <div className="border-t border-dark-700 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-white font-bold">Total payé:</span>
                  <span className={`text-lg font-bold ${
                    Math.abs(parseFloat(calculerTotal()) - parseFloat(montantTotal || '0')) > 0.01 
                      ? 'text-red-500' 
                      : 'text-primary-500'
                  }`}>
                    {calculerTotal()} €
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Boutons d'action */}
      <div className="flex justify-between pt-6 border-t border-dark-800">
        <button
          onClick={() => navigate('/shared-expenses')}
          className="px-6 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-lg font-medium transition-colors duration-200"
        >
          Annuler
        </button>
        
        <button
          onClick={handleUpdateDepense}
          disabled={loading}
          className="flex items-center space-x-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-dark-700 text-white rounded-lg font-medium transition-colors duration-200"
        >
          <Save className="w-5 h-5" />
          <span>Mettre à jour</span>
        </button>
      </div>
    </div>
  );
};

export default EditSharedExpense; 